/**
 * A table the assistant rendered in a reply. When the table names a resource kind the console has a page for, each
 * row with an id links its first cell to that page. A cell value longer than CELL_MAX_LENGTH is cropped, and its
 * tooltip shows the full value.
 */

import { Component, Input } from '@angular/core';
import { AssistantTable } from './api/assistant.service';
import { AssistantScopeKind } from './api/assistant-scope';
import { resourceLink } from './assistant-links.util';

/** The longest cell value the table shows in full. */
export const CELL_MAX_LENGTH = 40;

@Component({
    selector: 'app-assistant-table',
    standalone: false,
    template: `
        <figure class="assistant-table">
            @if (table.title) {
                <figcaption>{{ table.title }}</figcaption>
            }
            <div class="assistant-table-scroll">
                <table>
                    <thead>
                        <tr>
                            @for (column of table.columns; track $index) {
                                <th scope="col">
                                    <span class="assistant-table-heading">{{ column }}</span>
                                </th>
                            }
                        </tr>
                    </thead>
                    <tbody>
                        @for (row of table.rows; track $index) {
                            <tr>
                                @for (cell of row.cells; track $index; let first = $first) {
                                    <td [attr.title]="isCropped(cell) ? cell : null">
                                        @if (first && link(row.id); as commands) {
                                            <a [routerLink]="commands">{{ crop(cell) }}</a>
                                        } @else {
                                            {{ crop(cell) }}
                                        }
                                    </td>
                                }
                            </tr>
                        }
                    </tbody>
                </table>
            </div>
        </figure>
    `,
    styles: [
        `
            .assistant-table {
                margin: 0.75rem 0 0 0;
            }
            figcaption {
                font-weight: 600;
                margin-bottom: 0.375rem;
            }
            .assistant-table-scroll {
                overflow-x: auto;
            }
            /* The chat bubble breaks words anywhere; a table cell keeps its words whole and wraps only at spaces and
               hyphens. */
            table {
                width: 100%;
                border-collapse: collapse;
                font-size: 0.8125rem;
                word-break: normal;
                overflow-wrap: normal;
            }
            th,
            td {
                padding: 0.25rem 0.5rem;
                border-bottom: 0.0625rem solid var(--inputBorder);
                text-align: left;
                vertical-align: top;
            }
            th {
                font-weight: 600;
                color: var(--subText);
            }
            /* A heading asks only for the width of its longest word, so the data sizes the column and the heading wraps
               between words to fit it. */
            .assistant-table-heading {
                display: block;
                width: min-content;
                min-width: 100%;
            }
            /* ziti-console-lib's global stylesheet sets text-decoration: none !important on every link. These selectors
               are more specific, so their !important wins and the row links look like links. */
            a {
                color: var(--primaryColor) !important;
                text-decoration: underline !important;
                text-decoration-line: underline !important;
                text-decoration-thickness: auto !important;
                text-decoration-color: currentColor !important;
                text-underline-offset: 0.125rem;
                cursor: pointer;
            }
            a:hover {
                color: var(--primaryColorHover) !important;
                text-decoration-thickness: 0.125rem !important;
            }
            a:focus-visible {
                outline: 0.125rem solid var(--primaryColor);
                outline-offset: 0.125rem;
                border-radius: 0.125rem;
            }
        `,
    ],
})
export class AssistantTableComponent {
    @Input({ required: true }) table!: AssistantTable;
    /** The scope the transcript belongs to, which decides the pages a row can link to. */
    @Input() scope?: AssistantScopeKind;

    link(id: string | null | undefined): string[] | null {
        return resourceLink(this.scope, this.table.kind, id);
    }

    /**
     * The cell value, cut to CELL_MAX_LENGTH characters plus `...` when it is longer. Counts code points, so it never
     * splits an emoji.
     */
    crop(cell: string | null | undefined): string {
        const value = cell ?? '';
        return this.isCropped(value) ? Array.from(value).slice(0, CELL_MAX_LENGTH).join('') + '...' : value;
    }

    isCropped(cell: string | null | undefined): boolean {
        return Array.from(cell ?? '').length > CELL_MAX_LENGTH;
    }
}
